// BackupPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaDownload, FaTable } from 'react-icons/fa';
import axios from 'axios';
import './CreateCustomerPage.css';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';
function BackupPage() {
  const navigate = useNavigate();
  const [backups, setBackups] = useState([]);
  const [maxBackups, setMaxBackups] = useState(parseInt(localStorage.getItem('numberOfBackups')) || 5);
  const [warningMessage, setWarningMessage] = useState('');
  const [warningType, setWarningType] = useState('warning');
  const [pendingAction, setPendingAction] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [backupInterval, setBackupInterval] = useState(6);
  const [newInterval, setNewInterval] = useState(6);
  const [baseUrl, setBaseUrl] = useState("");
  const [companyDetails, setCompanyDetails] = useState(null);
  
  // Branch & Multi-tenancy State
  const [branches, setBranches] = useState([]);
  const [assignedCompanies, setAssignedCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [selectedBackupTypes, setSelectedBackupTypes] = useState(['all']);
  // Permissions
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [permsLoading, setPermsLoading] = useState(true);
  const [showPermModal, setShowPermModal] = useState(false);
  const [permModalMsg, setPermModalMsg] = useState("");

  const fetchPermissions = async (currentBaseUrl) => {
    try {
      setPermsLoading(true);
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        const roleRaw = userObj.role || userObj.UserType || '';
        
        const isAdminRole = checkIsAdmin(userObj);
        const isGroupAdminRole = checkIsGlobalAdmin(userObj);
        
        setUserRole(roleRaw);

        if (roleRaw) {
          const url = `${currentBaseUrl}/api/role-permissions?role=${encodeURIComponent(roleRaw)}`;
          const response = await axios.get(url);
          const perms = response.data.permissions || [];
          const pagePerm = perms.find(p => p.pageId === 'backup');

          if (isAdminRole || isGroupAdminRole) {
            setCanRead(true);
            setCanWrite(true);
            setCanDelete(true);
            setCanCreate(true);
          } else if (pagePerm) {
            setCanRead(pagePerm.canRead === true);
            setCanWrite(pagePerm.canWrite === true);
            setCanDelete(pagePerm.canDelete === true);
            setCanCreate(pagePerm.canCreate === true);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setPermsLoading(false);
    }
  };

  const fetchBranches = async (currentBaseUrl, targetCompany = null) => {
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const company_name = targetCompany || (selectedCompanies.length > 0 ? selectedCompanies.join(',') : "") || userObj.company || "";
      
      if (!company_name) {
        setBranches([]);
        return;
      }

      const response = await axios.get(`${currentBaseUrl || baseUrl}/api/branches`, {
        headers: { 'X-Company-Name': company_name }
      });
      setBranches(response.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };
  const computeTotalHours = (opening, closing) => {
    if (!opening || !closing) return 0;
    const [oh, om] = opening.split(':').map(Number);
    const [ch, cm] = closing.split(':').map(Number);
    let totalMin = (ch * 60 + cm) - (oh * 60 + om);
    if (totalMin < 0) totalMin += 1440;
    return totalMin / 60;
  };
  const totalHours = companyDetails ? computeTotalHours(companyDetails.openingTime, companyDetails.closingTime) : 0;
  const extendedHours = totalHours + 2;
  const numBackupsPreview = newInterval > 0 ? Math.floor(extendedHours / newInterval) : 0;
  useEffect(() => {
    const fetchConfig = async () => {
      let currentBaseUrl = "";
      try {
        const response = await axios.get("/api/network_info");
        const { config: appConfig } = response.data;
        if (appConfig.mode === "client") {
          currentBaseUrl = `http://${appConfig.server_ip}:6034`;
          setBaseUrl(currentBaseUrl);
        } else {
          setBaseUrl("");
        }
      } catch (error) {
        console.error("Failed to fetch config:", error);
        setBaseUrl("");
      } finally {
        // Fetch data using currentBaseUrl
        fetchBackupInfo(currentBaseUrl);
        fetchBackupInterval(currentBaseUrl);
        fetchCompanyDetails(currentBaseUrl);
        fetchPermissions(currentBaseUrl);
        // Initialize Multi-tenant context
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userObj = JSON.parse(userStr);
          setUserRole(userObj.role);
          const comps = userObj.companies || (userObj.company ? [userObj.company] : []);
          setAssignedCompanies(comps);
          const initialComp = (userObj.role === 'group_admin') ? "" : (userObj.company || (comps.length > 0 ? comps[0] : ""));
          setSelectedCompanies(initialComp ? [initialComp] : []);
          if (initialComp) {
            fetchBranches(currentBaseUrl, initialComp);
            fetchBackupInfo(currentBaseUrl, initialComp);
          } else if (userObj.role === 'group_admin') {
            // For group admins, we don't fetch branches or backup info until companies are selected
            setBranches([]);
            setBackups([]);
          }
        }
        // Set maxBackups from localStorage (local operation, no URL needed)
        const storedMax = parseInt(localStorage.getItem('numberOfBackups')) || 5;
        setMaxBackups(storedMax);
      }
    };
    fetchConfig();
  }, []);
  const fetchCompanyDetails = async (currentBaseUrl) => {
    try {
      const response = await axios.get(`${currentBaseUrl}/api/company-details`);
      if (response.data.companyDetails && response.data.companyDetails.length > 0) {
        const latest = response.data.companyDetails[response.data.companyDetails.length - 1];
        setCompanyDetails(latest);
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  };
  const fetchBackupInterval = async (currentBaseUrl) => {
    try {
      const response = await axios.get(`${currentBaseUrl}/api/get-backup-interval`);
      setBackupInterval(response.data.interval);
      setNewInterval(response.data.interval);
    } catch (error) {
      console.error('Error fetching backup interval:', error);
      setWarningMessage(`Failed to fetch backup interval: ${error.message}`);
      setWarningType('warning');
    }
  };
  const handleSetInterval = async () => {
    if (!canWrite) {
      setPermModalMsg("You do not have permission to change backup settings.");
      setShowPermModal(true);
      return;
    }
    try {
      await axios.post(`${baseUrl}/api/set-backup-interval`, { interval: newInterval });
      setBackupInterval(newInterval);
      setWarningMessage('Backup interval updated successfully!');
      setWarningType('success');
    } catch (error) {
      console.error('Error setting backup interval:', error);
      setWarningMessage(`Failed to set backup interval: ${error.message}`);
      setWarningType('warning');
    }
  };
  const handleWarningOk = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setWarningMessage('');
    setWarningType('warning');
  };
  const fetchBackupInfo = async (currentBaseUrl, targetCompany = null) => {
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const company_name = targetCompany || (selectedCompanies.length > 0 ? selectedCompanies.join(',') : "") || userObj.company;
      
      if (!company_name || company_name.toLowerCase() === 'all companies') {
        setBackups([]);
        return;
      }

        const isAdmin = checkIsAdmin(userObj);
        const isGlobal = checkIsGlobalAdmin(userObj);

        const headers = {
          'X-Company-Name': company_name,
          'X-Branch-Name': (isAdmin || isGlobal) ? (selectedBranches.length > 0 ? selectedBranches.join(',') : "") : (userObj.branch_name || "")
        };


      const response = await axios.get(`${currentBaseUrl || baseUrl}/api/backup-info`, { headers });
      const sortedData = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      const limitedData = sortedData.slice(0, maxBackups);
      setBackups(limitedData);
    } catch (error) {
      console.error('Error fetching backup info:', error);
      setWarningMessage(`Failed to fetch backup info: ${error.message}`);
      setWarningType('warning');
    }
  };
  const handleGoBack = () => {
    navigate('/admin');
  };
  const handleBackup = async () => {
    if (!canWrite) {
      setPermModalMsg("You do not have permission to create backups.");
      setShowPermModal(true);
      return;
    }
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const companyHeaders = selectedCompanies.length > 0 ? selectedCompanies.join(',') : (userObj.company || "");
      const branchHeaders = selectedBranches.length > 0 ? selectedBranches.join(',') : (userObj.branch_name || "");

      const isAdmin = checkIsAdmin(userObj);
      const isGlobal = checkIsGlobalAdmin(userObj);

      const headers = {
        'X-Company-Name': companyHeaders,
        'X-Branch-Name': (isAdmin || isGlobal) ? branchHeaders : userObj.branch_name,
        'X-Backup-Collections': selectedBackupTypes.length > 0 ? selectedBackupTypes.join(',') : 'all'
      };

      const response = await axios.get(`${baseUrl}/api/backup-to-excel`, {
        responseType: 'blob',
        headers: headers
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
      const filename = `backup_restaurant_data_${timestamp}.xlsx`;
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccessMsg('Backup created successfully! Excel file downloaded and sent to configured email.');
      setShowSuccessModal(true);
      fetchBackupInfo(baseUrl);
    } catch (error) {
      console.error('Backup error:', error);
      let errorMessage = 'Failed to create backup';
      
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json.error || text;
        } catch (e) {
          console.error("Failed to parse blob error response", e);
        }
      } else if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setWarningMessage(errorMessage);
      setWarningType('warning');
    }
  };
  const handleDownloadBackup = async (filename) => {
    if (!canWrite) {
      setPermModalMsg("You do not have permission to download backups.");
      setShowPermModal(true);
      return;
    }
    try {
      const response = await axios.post(`${baseUrl}/api/download-backup`, { filename }, {
        responseType: 'blob',
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccessMsg('Backup downloaded successfully!');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Download backup error:', error);
      let errorMessage = 'Failed to download backup';
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setWarningMessage(errorMessage);
      setWarningType('warning');
    }
  };
  const handleSetMaxBackups = (newMax) => {
    if (!canWrite) {
      setPermModalMsg("You do not have permission to change backup settings.");
      setShowPermModal(true);
      return;
    }
    setMaxBackups(newMax);
    localStorage.setItem('numberOfBackups', newMax);
    fetchBackupInfo(baseUrl); // Refresh the list with new limit using baseUrl
  };

  if (permsLoading) {
    return (
      <div className="create-customer-container main-customer-page">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center', color: '#604BE8', fontSize: '18px', fontWeight: '600' }}>
            Loading Permissions...
          </div>
        </div>
      </div>
    );
  }

  const userStr = localStorage.getItem('user');
  const userObj = userStr ? JSON.parse(userStr) : {};
  const isGlobalRole = checkIsGlobalAdmin(userObj);
  const isAdminRole = checkIsAdmin(userObj);

  if (!canRead && !isAdminRole && !permsLoading) {
    return (
      <div className="create-customer-container main-customer-page">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ background: '#fff', padding: '40px', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', textAlign: 'center', maxWidth: '400px' }}>
            <div style={{ fontSize: '48px', color: '#ef4444', marginBottom: '20px' }}>🔒</div>
            <h2 style={{ color: '#1f2937', marginBottom: '10px' }}>Access Denied</h2>
            <p style={{ color: '#4b5563', marginBottom: '20px' }}>You do not have permission to view Backup Management.</p>
            <button onClick={() => navigate('/admin')} style={{ padding: '10px 20px', background: '#604BE8', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-customer-container main-customer-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        .action-btn {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }
        .action-btn-download {
          background: rgba(96, 75, 232, 0.15);
          color: #604BE8;
        }
        .action-btn-download:hover {
          background: #604BE8 !important;
          color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(96, 75, 232, 0.3);
        }
        .modern-input-field {
          width: 100%;
          height: 44px;
          padding: 0 16px;
          border-radius: 8px;
          border: 1px solid transparent;
          background: #F3F6FB;
          font-size: 14px;
          font-weight: 600;
          color: #1B1B29;
          outline: none;
          transition: all 0.2s ease;
        }
        .modern-input-field:focus {
          border: 1px solid #604BE8;
          box-shadow: 0 0 0 3px rgba(96, 75, 232, 0.1);
        }
        .premium-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }
        .premium-table th {
          background: #06D6A0;
          color: #ffffff;
          font-weight: 600;
          font-size: 15px;
          text-transform: capitalize;
          letter-spacing: 0.3px;
          padding: 16px 24px;
          text-align: left;
          border: none;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .premium-table th:first-child { border-top-left-radius: 10px; }
        .premium-table th:last-child { border-top-right-radius: 10px; }
        .premium-table td {
          padding: 16px 24px;
          color: #4A4A5A;
          font-size: 14px;
          font-weight: 500;
          border-bottom: 1px solid #F1F3F8;
          transition: all 0.2s ease;
          vertical-align: middle;
        }
        .premium-table tr:nth-child(odd) td { background: #ffffff; }
        .premium-table tr:nth-child(even) td { background: #FAFBFD; }
        .premium-table tr:hover td { background: #F1F4F9; }
        .premium-table tr:last-child td { border-bottom: none; }
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {showPermModal && (
        <div className="premium-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="premium-modal-box" style={{ background: '#ffffff', borderRadius: '24px', width: '90%', maxWidth: '420px', padding: '32px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', animation: 'modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <div style={{ width: '84px', height: '84px', background: '#FEE2E2', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', color: '#EF4444', fontSize: '36px' }}>🔒</div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', fontWeight: '800', color: '#0F172A' }}>Permission Denied</h3>
            <p style={{ margin: '0 0 32px 0', fontSize: '15px', color: '#64748B', lineHeight: '1.6' }}>{permModalMsg}</p>
            <button onClick={() => setShowPermModal(false)} style={{ height: '48px', padding: '0 24px', borderRadius: '14px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s ease', background: '#604BE8', color: '#ffffff', border: 'none', width: '100%' }}>Understand</button>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '24px', width: '90%', maxWidth: '420px',
            padding: '40px 32px', textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            animation: 'modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>
            <div style={{ width: '80px', height: '80px', background: '#ECFDF5', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: '36px' }}>✅</div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', fontWeight: '800', color: '#0F172A' }}>Success!</h3>
            <p style={{ margin: '0 0 32px 0', fontSize: '15px', color: '#64748B', lineHeight: '1.6', fontWeight: '500' }}>{successMsg}</p>
            <button
              onClick={() => setShowSuccessModal(false)}
              style={{ height: '48px', padding: '0 24px', borderRadius: '14px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', background: '#10B981', color: '#ffffff', border: 'none', width: '100%', transition: 'all 0.2s ease' }}
              onMouseOver={e => e.currentTarget.style.background = '#059669'}
              onMouseOut={e => e.currentTarget.style.background = '#10B981'}
            >OK</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '36px 48px 16px 48px' }}>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <button
            onClick={handleGoBack}
            style={{ height: '44px', padding: '0 20px', borderRadius: '10px', border: '1px solid #E2E6EF', background: '#ffffff', color: '#604BE8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
            onMouseOver={e => { e.currentTarget.style.borderColor = '#604BE8'; e.currentTarget.style.background = '#eff2f9'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = '#E2E6EF'; e.currentTarget.style.background = '#ffffff'; }}
          >
            <FaArrowLeft style={{ fontSize: '12px' }} /> Back to Admin
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#1B1B29', letterSpacing: '-0.5px' }}>System Backup Center</h1>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleBackup}
            style={{ height: '44px', padding: '0 24px', background: '#604BE8', color: '#ffffff', border: 'none', borderRadius: '10px', fontWeight: '600', fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 15px rgba(96, 75, 232, 0.25)', transition: 'all 0.2s' }}
            onMouseOver={e => { e.currentTarget.style.backgroundColor = '#4B36C8'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseOut={e => { e.currentTarget.style.backgroundColor = '#604BE8'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            📦 Generate New System Backup
          </button>
        </div>
      </div>

      <div className="customer-scroll-body">
        {warningMessage && (
          <div style={{
            padding: '12px 20px', borderRadius: '12px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: '#FEE2E2', color: '#DC2626',
            borderLeft: '4px solid #EF4444', fontWeight: '600'
          }}>
            <span>{warningMessage}</span>
            <button onClick={() => setWarningMessage('')} style={{ background: 'transparent', border: 'none', color: 'inherit', fontWeight: 'bold', cursor: 'pointer', fontSize: '20px' }}>&times;</button>
          </div>
        )}

        <div className="form-sections-wrapper">
          

          {isGlobalRole && (
            <div className="form-section-card theme-purple">
              <div className="section-header">
                <h2>Target Companies Selection :</h2>
              </div>
              <div style={{ padding: '10px 24px 24px 24px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#eff2f9', borderRadius: '12px', border: '1.5px solid #C3CDE4', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1B1B29' }}>
                    <input type="checkbox" checked={selectedCompanies.length === assignedCompanies.length && assignedCompanies.length > 0} onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCompanies(assignedCompanies);
                        fetchBranches(baseUrl, assignedCompanies.join(','));
                        fetchBackupInfo(baseUrl, assignedCompanies.join(','));
                      } else {
                        setSelectedCompanies([]); setBranches([]); setSelectedBranches([]); setBackups([]);
                      }
                    }} style={{ width: '18px', height: '18px', accentColor: '#604BE8' }} />
                    ENABLE ALL ASSIGNED COMPANIES
                  </label>
                  {assignedCompanies.map((comp, idx) => (
                    <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: selectedCompanies.includes(comp) ? '#EEF2FF' : '#F8FAFC', borderRadius: '12px', border: `1.5px solid ${selectedCompanies.includes(comp) ? '#C7D2FE' : '#E2E8F0'}`, cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1B1B29' }}>
                      <input type="checkbox" checked={selectedCompanies.includes(comp)} onChange={(e) => {
                        let newComps;
                        if (e.target.checked) newComps = [...selectedCompanies, comp];
                        else newComps = selectedCompanies.filter(c => c !== comp);
                        setSelectedCompanies(newComps);
                        if (newComps.length > 0) { fetchBranches(baseUrl, newComps.join(',')); fetchBackupInfo(baseUrl, newComps.join(',')); }
                        else { setBranches([]); setSelectedBranches([]); setBackups([]); }
                      }} style={{ width: '18px', height: '18px', accentColor: '#604BE8' }} />
                      {comp}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {branches.length > 0 && (selectedCompanies.length > 0 || !isGlobalRole) && (isAdminRole || isGlobalRole) && (
            <div className="form-section-card theme-green">
              <div className="section-header">
                <h2>Target Branches Selection :</h2>
              </div>
              <div style={{ padding: '10px 24px 24px 24px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#ECFDF5', borderRadius: '12px', border: '1.5px solid #D1FAE5', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1B1B29' }}>
                    <input type="checkbox" checked={selectedBranches.length === branches.length && branches.length > 0} onChange={(e) => {
                      const newBranches = e.target.checked ? branches.map(b => b.branch_name || b) : [];
                      setSelectedBranches(newBranches);
                      setTimeout(() => fetchBackupInfo(baseUrl), 100);
                    }} style={{ width: '18px', height: '18px', accentColor: '#10B981' }} />
                    SELECT ALL BRANCHES
                  </label>
                  {branches.map((branch, idx) => {
                    const branchName = branch.branch_name || branch;
                    return (
                      <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: selectedBranches.includes(branchName) ? '#ECFDF5' : '#F8FAFC', borderRadius: '12px', border: `1.5px solid ${selectedBranches.includes(branchName) ? '#D1FAE5' : '#E2E8F0'}`, cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1B1B29' }}>
                        <input type="checkbox" checked={selectedBranches.includes(branchName)} onChange={(e) => {
                          let newBranches;
                          if (e.target.checked) newBranches = [...selectedBranches, branchName];
                          else newBranches = selectedBranches.filter(b => b !== branchName);
                          setSelectedBranches(newBranches);
                          setTimeout(() => fetchBackupInfo(baseUrl), 100);
                        }} style={{ width: '18px', height: '18px', accentColor: '#10B981' }} />
                        {branchName}
                      </label>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="form-section-card theme-orange">
            <div className="section-header">
              <h2>Target Datasets Selection :</h2>
            </div>
            <div style={{ padding: '10px 24px 24px 24px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#FFF7ED', borderRadius: '12px', border: '1.5px solid #FFEDD5', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1B1B29' }}>
                  <input type="checkbox" checked={selectedBackupTypes.includes('all') || selectedBackupTypes.length === 0} onChange={(e) => {
                    if (e.target.checked) setSelectedBackupTypes(['all']);
                  }} style={{ width: '18px', height: '18px', accentColor: '#F97316' }} />
                  BACKUP ALL DATA (Full System Backup)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: selectedBackupTypes.includes('items') ? '#FFF7ED' : '#F8FAFC', borderRadius: '12px', border: `1.5px solid ${selectedBackupTypes.includes('items') ? '#FFEDD5' : '#E2E8F0'}`, cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1B1B29' }}>
                  <input type="checkbox" checked={selectedBackupTypes.includes('items')} onChange={(e) => {
                    if (e.target.checked) setSelectedBackupTypes(prev => prev.filter(t => t !== 'all').concat('items'));
                    else setSelectedBackupTypes(prev => prev.filter(t => t !== 'items'));
                  }} style={{ width: '18px', height: '18px', accentColor: '#F97316' }} />
                  Items (Excludes Price)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: selectedBackupTypes.includes('purchase_items') ? '#FFF7ED' : '#F8FAFC', borderRadius: '12px', border: `1.5px solid ${selectedBackupTypes.includes('purchase_items') ? '#FFEDD5' : '#E2E8F0'}`, cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1B1B29' }}>
                  <input type="checkbox" checked={selectedBackupTypes.includes('purchase_items')} onChange={(e) => {
                    if (e.target.checked) setSelectedBackupTypes(prev => prev.filter(t => t !== 'all').concat('purchase_items'));
                    else setSelectedBackupTypes(prev => prev.filter(t => t !== 'purchase_items'));
                  }} style={{ width: '18px', height: '18px', accentColor: '#F97316' }} />
                  Purchase Items (Excludes Price)
                </label>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="form-section-card theme-blue">
              <div className="section-header">
                <h2>Auto-Interval Settings :</h2>
              </div>
              <div style={{ padding: '10px 24px 24px 24px' }}>
                <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '15px', fontWeight: '500' }}>Define how frequently the system triggers automatic backups.</p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <div style={{ flex: '1', maxWidth: '120px' }}>
                    <input type="number" value={newInterval} onChange={(e) => setNewInterval(parseInt(e.target.value) || 1)} min={1} className="modern-input-field" />
                  </div>
                  <button onClick={handleSetInterval} style={{ height: '44px', padding: '0 20px', background: '#604BE8', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>Update</button>
                </div>
                <div style={{ marginTop: '15px', fontSize: '13px', color: '#604BE8', background: '#EEF2FF', display: 'inline-block', padding: '6px 12px', borderRadius: '8px', fontWeight: '700' }}>Current: every {backupInterval} hours</div>
              </div>
            </div>
            
            <div className="form-section-card theme-purple">
              <div className="section-header">
                <h2>History Limit Settings :</h2>
              </div>
              <div style={{ padding: '10px 24px 24px 24px' }}>
                <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '15px', fontWeight: '500' }}>Choose how many recent backups to keep visible.</p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <div style={{ flex: '1', maxWidth: '120px' }}>
                    <input type="number" value={maxBackups} onChange={(e) => handleSetMaxBackups(parseInt(e.target.value) || 5)} min={1} className="modern-input-field" />
                  </div>
                </div>
                <div style={{ marginTop: '15px', fontSize: '13px', color: '#7E22CE', background: '#FAF5FF', display: 'inline-block', padding: '6px 12px', borderRadius: '8px', fontWeight: '700' }}>Displaying {maxBackups} snapshots</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="form-section-card theme-green">
              <div className="section-header">
                <h2>Data Import Wizard :</h2>
              </div>
              <div style={{ padding: '10px 24px 24px 24px' }}>
                <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '15px', fontWeight: '500' }}>Restore or migrate data from Excel/JSON files.</p>
                <button onClick={() => navigate('/import-data')} style={{ width: '100%', height: '44px', background: '#10B981', color: '#fff', borderRadius: '8px', border: 'none', fontWeight: '700', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                  <FaTable /> Go to Import Wizard
                </button>
                <div style={{ marginTop: '15px', fontSize: '13px', color: '#059669', background: '#ECFDF5', display: 'inline-block', padding: '6px 12px', borderRadius: '8px', fontWeight: '700' }}>Supports Excel & JSON</div>
              </div>
            </div>

            <div className="form-section-card theme-orange">
              <div className="section-header">
                <h2>Operating Context :</h2>
              </div>
              <div style={{ padding: '10px 24px 24px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', fontSize: '14px' }}>
                  {companyDetails ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
                        <span style={{ color: '#64748b', fontWeight: '600' }}>Store Timings:</span> <strong style={{ color: '#1B1B29' }}>{companyDetails.openingTime || 'N/A'} - {companyDetails.closingTime || 'N/A'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
                        <span style={{ color: '#64748b', fontWeight: '600' }}>Operational Span:</span> <strong style={{ color: '#1B1B29' }}>{totalHours.toFixed(1)} hrs</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b', fontWeight: '600' }}>Backup Preview:</span> <strong style={{ color: '#1B1B29' }}>~{numBackupsPreview} backups per cycle</strong>
                      </div>
                    </>
                  ) : (
                    <div style={{ fontStyle: 'italic', color: '#94a3b8', fontWeight: '500' }}>Company details pending...</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div style={{ background: '#ffffff', borderRadius: '10px', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)', border: '1px solid #E2E6EF', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #F1F3F8' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1B1B29' }}>Recent Backups</h2>
              <span style={{ background: '#F3F6FB', color: '#1B1B29', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '800' }}>{backups.length} stored</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Format</th>
                    <th>Size</th>
                    <th style={{ textAlign: 'right', paddingRight: '36px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.length > 0 ? backups.map((backup, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: '700', color: '#1B1B29' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '20px' }}>📁</span>
                          {backup.date}
                        </div>
                      </td>
                      <td>
                        <span style={{ padding: '4px 12px', borderRadius: '6px', background: '#eff2f9', color: '#604BE8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase' }}>Excel</span>
                      </td>
                      <td>{backup.size}</td>
                      <td style={{ textAlign: 'right', paddingRight: '36px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button onClick={() => handleDownloadBackup(backup.filename)} title="Download Backup" className="action-btn action-btn-download">
                            <FaDownload style={{ fontSize: '18px' }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" style={{ padding: '60px', textAlign: 'center', color: '#94a3b8', fontWeight: '600', fontSize: '15px' }}>
                        <div style={{ fontSize: '42px', marginBottom: '16px' }}>📂</div>
                        <div>No backups found.</div>
                        <div style={{ fontSize: '13px', marginTop: '8px', color: '#cbd5e1' }}>Click "Generate New System Backup" to create your first backup.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default BackupPage;
